export const norm = (s) => (s || '').toString().trim().toUpperCase().replace(/\s+/g, '');

export const fieldMatches = (a, b) => {
  const x = norm(a);
  const y = norm(b);
  if (!y) return true;
  if (!x) return true;
  if (x === y) return true;
  if (x.includes(y) || y.includes(x)) return true;
  return false;
};

/** Parse size từ chuỗi order: `N: ... - Size: XL - Custom: ...` */
export const parseOrderLabelSize = (sizeStr) => {
  if (!sizeStr) return null;
  const raw = String(sizeStr).replace(/\*/g, '');
  const parts = raw.split(/\s*-\s*/);
  for (const part of parts) {
    const m = part.trim().match(/^Size:\s*(.+)$/i);
    if (m) return m[1].trim();
  }
  return null;
};

export const parseOrderSizeColor = (item) => {
  const rawSize = item?.size || '';
  const orderColor = item?.color;
  let orderSize = parseOrderLabelSize(rawSize);
  let sizeArray = null;

  if (!orderSize) {
    let size = rawSize
      .replace('Type: ', '')
      .replace('Fabric Brand: ', '')
      .replace('Style: ', '')
      .replace(/'/g, '')
      .replace('Product Sides Options: ', '')
      .replace('Product Sides: ', '')
      .replace(/ /g, '');

    const compoundStyles = ['WomensV-NeckT-Shirt', 'WomenT-Shirt', 'YouthT-Shirt', 'UnisexT-Shirt', 'V-NeckT-Shirt'].sort(
      (a, b) => b.length - a.length
    );
    const stylePlaceholders = {};
    let stylePhIdx = 0;
    for (const st of compoundStyles) {
      const esc = st.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      size = size.replace(new RegExp(esc, 'g'), () => {
        const token = `__ST${stylePhIdx}__`;
        stylePlaceholders[token] = st;
        stylePhIdx += 1;
        return token;
      });
    }
    size = size.replace(/T-Shirt/g, 'TShirt');
    sizeArray = size.split('-').map((part) => stylePlaceholders[part] || part);
    const bareTshirtToStyle = { Women: 'WomenT-Shirt', Woman: 'WomenT-Shirt', Unisex: 'UnisexT-Shirt', Youth: 'YouthT-Shirt' };
    if (sizeArray[2] === 'TShirt' && bareTshirtToStyle[sizeArray?.[0]]) {
      sizeArray[2] = bareTshirtToStyle[sizeArray[0]];
    }
    orderSize = rawSize.includes('Size: US') ? sizeArray?.[2] : sizeArray?.[3];
  }

  return { orderSize, orderColor, sizeArray };
};

/** Option chỉ có 1 value → tự chọn, không bắt user chọn lại */
export const applySingleValueOptions = (catalog, picks = {}) => {
  const next = { ...picks };
  for (const opt of catalog?.options || []) {
    const values = opt?.values || [];
    if (values.length === 1) {
      next[opt.option] = values[0];
    }
  }
  return next;
};

export const getCatalogOptionKeys = (catalog) => (catalog?.options || []).map((o) => o.option);

export const buildPicksFromOrder = (catalog, orderSize, orderColor) => {
  const picks = {};
  for (const key of getCatalogOptionKeys(catalog)) {
    if (key === 'size' && orderSize) picks.size = orderSize;
    if (key === 'color' && orderColor) picks.color = orderColor;
  }
  return picks;
};

export const variantMatchesPicks = (catalog, variant, picks) => {
  const keys = getCatalogOptionKeys(catalog);
  const selected = keys.filter((k) => picks[k]);
  if (!selected.length) return false;
  return selected.every((k) => variant[k] && fieldMatches(variant[k], picks[k]));
};

export const findLuxuryProVariant = (catalog, picks) => {
  const keys = getCatalogOptionKeys(catalog);
  if (!keys.every((k) => picks[k])) return null;
  return (catalog.variants || []).find((v) => keys.every((k) => fieldMatches(v[k], picks[k]))) || null;
};

export const resolveLuxuryPro = (catalog, orderItem, manualPicks = {}) => {
  const { orderSize, orderColor } = parseOrderSizeColor(orderItem);
  const keys = getCatalogOptionKeys(catalog);

  let picks = buildPicksFromOrder(catalog, orderSize, orderColor);
  picks = applySingleValueOptions(catalog, picks);
  picks = { ...picks, ...manualPicks };
  picks = applySingleValueOptions(catalog, picks);

  let variant = null;

  if (keys.every((k) => picks[k])) {
    variant = findLuxuryProVariant(catalog, picks);
  }

  if (!variant) {
    const provided = keys.filter((k) => picks[k]);
    if (provided.length) {
      const candidates = (catalog.variants || []).filter((v) =>
        provided.every((k) => v[k] && fieldMatches(v[k], picks[k]))
      );
      if (candidates.length === 1) {
        variant = candidates[0];
        keys.forEach((k) => {
          if (variant[k]) picks[k] = variant[k];
        });
      }
    }
  }

  return {
    orderSize,
    orderColor,
    picks,
    variant,
    matched: !!variant,
    catalogSku: catalog?.sku || null,
    variant_id: variant?.variant_id || null,
  };
};

export const orderHasDesignForFulfill = (item) =>
  (item.size.replace('*', '').includes('Both Sides') && item.design_link?.front && item.design_link?.back) ||
  ((item.size.replace('*', '').includes('Front Side Only') || item.size.replace('*', '').includes('Front Side only')) &&
    item.design_link?.front) ||
  ((item.size.replace('*', '').includes('Back Side Only') || item.size.replace('*', '').includes('Back Side only')) &&
    item.design_link?.back) ||
  (item.size.replace('*', '').includes('Size: US') && item.design_link?.front);

/** LuxuryPro: chỉ cần có ít nhất front hoặc back design */
export const orderHasDesignForLuxuryPro = (item) => {
  const hasDesign = (v) => v != null && String(v).trim() !== '';
  return hasDesign(item?.design_link?.front) || hasDesign(item?.design_link?.back);
};

const CUSTOM_PART_PATTERNS = [
  [/^custom\s*name(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Custom Name'],
  [/^custom\s*number(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Custom Number'],
  [/^customize\s*name(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Customize Name'],
  [/^customize\s*number(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Customize Number'],
  [/^customize\s*jersey\s*name(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Customize Jersey Name'],
  [/^customize\s*jersey\s*number(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Customize Jersey Number'],
  [/^custom\s*jersey\s*name(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Custom Jersey Name'],
  [/^custom\s*jersey\s*number(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Custom Jersey Number'],
  [/^customize\s*name\/\s*(.*)$/i, 'Customize Name/'],
  [/^customize(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Customize'],
  [/^custom(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Custom'],
  [/^name(?:\s*\([^)]*\))?\s*:\s*(.*)$/i, 'Name'],
];

const isCustomSizePart = (part) => {
  const p = String(part || '').trim();
  if (!p) return false;
  return CUSTOM_PART_PATTERNS.some(([re]) => re.test(p));
};

const normalizeCustomPart = (part) => {
  const p = String(part || '').trim();
  for (const [re, label] of CUSTOM_PART_PATTERNS) {
    const m = p.match(re);
    if (m) return `${label}: ${m[1].trim()}`;
  }
  return p;
};

export const extractCustomPartsFromSize = (sizeStr) => {
  if (!sizeStr) return [];
  return String(sizeStr)
    .split(/\s*-\s*/)
    .map((p) => p.trim())
    .filter(isCustomSizePart)
    .map(normalizeCustomPart);
};

/** Build note gửi LuxuryPro — chỉ lấy custom từ chuỗi size */
export const buildLuxuryProNote = (rows) => {
  const parts = [];

  for (const row of rows || []) {
    const item = row?.items?.[0] || row;
    parts.push(...extractCustomPartsFromSize(item?.size));
  }

  const seen = new Set();
  const unique = [];
  for (const p of parts) {
    const key = p.toLowerCase().replace(/\s+/g, ' ');
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(p);
    }
  }

  return unique.join(' - ');
};


export const previewLuxuryProNote = (rows) => buildLuxuryProNote(rows);
